import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Megaphone, Sparkles, RotateCw, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";

const GOALS = ["Service", "Product", "Offer/Promotion", "General Brand Awareness"];
const AUDIENCES = [
  "All audiences", "Desk workers", "Athletes", "Chronic pain", "Injury recovery",
  "Stress management", "Pre/post-natal", "Seniors", "Travelers", "Sleep/wellness",
];
const SERVICES = [
  "Clinical Massage", "Neuromuscular Therapy", "Myofascial Release", "Deep Tissue",
  "Sports Recovery", "Recovery / Percussion Therapy", "General Consultation",
];
const TONES = ["Confident", "Educational", "Warm", "Urgent"];
const PLATFORMS = [
  { key: "instagram", label: "Instagram" }, { key: "facebook", label: "Facebook" }, { key: "linkedin", label: "LinkedIn" },
];

export default function AdCreator() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [form, setForm] = useState({
    goal: "Service", subject: "Clinical Massage", audience: "All audiences",
    platforms: ["instagram"], tone: "Confident", offer: "",
  });
  const [loading, setLoading] = useState(false);
  const [ads, setAds] = useState([]);
  const [usedIdx, setUsedIdx] = useState({});
  const [busyIdx, setBusyIdx] = useState(null);

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="ads-no-access">You don't have access to the Ad Creative Generator.</div>;
  }

  const togglePlatform = (k) => setForm((f) => {
    const has = f.platforms.includes(k);
    const next = has ? f.platforms.filter((p) => p !== k) : [...f.platforms, k];
    return { ...f, platforms: next.length ? next : [k] };
  });

  const generate = async () => {
    if (!form.platforms.length) { toast.error("Pick at least one platform"); return; }
    setLoading(true);
    try {
      const res = await api.post("/ads/generate", form);
      setAds(res.data.variations || []);
      setUsedIdx({});
      if (!(res.data.variations || []).length) toast.error("No ads returned — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ad generation failed");
    } finally { setLoading(false); }
  };

  const sendAd = async (ad, idx) => {
    setBusyIdx(idx);
    try {
      await api.post("/ads/use", { headline: ad.headline, body: ad.body, cta: ad.cta, platform: form.platforms[0] });
      setUsedIdx((s) => ({ ...s, [idx]: true }));
      toast.success("Ad sent to Approval Queue (Needs review)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create post"); }
    finally { setBusyIdx(null); }
  };

  return (
    <div className="space-y-8" data-testid="ad-creator-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>AI Ad Creator</h1>
        <p className="text-[#7A9090]">Generate 3 on-brand ad variations, then send the best to the approval queue.</p>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">What are we promoting?</Label>
            <select data-testid="ad-goal" value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
              {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Service / product</Label>
            <input list="ad-services" data-testid="ad-subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Type or pick…" className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white" />
            <datalist id="ad-services">{SERVICES.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Target audience</Label>
            <select data-testid="ad-audience" value={form.audience} onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
              {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Tone</Label>
            <select data-testid="ad-tone" value={form.tone} onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Platform</Label>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <button key={p.key} data-testid={`ad-platform-${p.key}`} onClick={() => togglePlatform(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${form.platforms.includes(p.key) ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Offer / details (optional)</Label>
          <Input data-testid="ad-offer" value={form.offer} onChange={(e) => setForm((f) => ({ ...f, offer: e.target.value }))} placeholder="e.g. 20% off first session in August" className="border-[#e5e7eb] text-[#3C4048]" />
        </div>
        <Button data-testid="generate-ads" onClick={generate} disabled={loading} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
          <Megaphone size={18} /> {loading ? "Generating 3 ads…" : "Generate 3 ad variations"}
        </Button>
      </div>

      {loading && <p className="text-[#7A9090]" data-testid="ads-loading">Writing your ads…</p>}

      {ads.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {ads.map((ad, idx) => (
            <div key={idx} data-testid={`ad-card-${idx}`} className="bg-white border border-[#e5e7eb] rounded-[12px] p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold"><Sparkles size={11} /> Variation {idx + 1}</span>
                <span className="text-[#7A9090] text-[10px] font-medium">{ad.format}</span>
              </div>
              <h3 className="text-[#3C4048] font-bold text-lg mb-2" style={{ fontFamily: "Montserrat", fontWeight: 800 }} data-testid={`ad-headline-${idx}`}>{ad.headline}</h3>
              <p className="text-[#3C4048] text-sm whitespace-pre-line mb-3" data-testid={`ad-body-${idx}`}>{ad.body}</p>
              <div className="inline-flex self-start items-center px-3 py-1 rounded-full bg-[#eef7f8] text-[#3A7A8C] text-xs font-semibold mb-3" data-testid={`ad-cta-${idx}`}>{ad.cta}</div>
              {ad.suggested_image_style && (
                <div className="flex items-start gap-1.5 text-[#7A9090] text-xs mb-4 border-t border-[#eef1f2] pt-3">
                  <ImageIcon size={14} className="mt-0.5 shrink-0" />
                  <span><span className="font-semibold">Suggested image:</span> {ad.suggested_image_style}</span>
                </div>
              )}
              <div className="mt-auto">
                <Button data-testid={`use-ad-${idx}`} disabled={busyIdx === idx || usedIdx[idx]} onClick={() => sendAd(ad, idx)} className="w-full bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-1 rounded-[10px]">
                  {usedIdx[idx] ? <><Check size={16} /> Sent to review</> : <>Use this</>}
                </Button>
              </div>
            </div>
          ))}
          <div className="md:col-span-3">
            <Button data-testid="regenerate-ads" onClick={generate} disabled={loading} variant="outline" className="border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] gap-1 rounded-[12px]">
              <RotateCw size={16} /> Regenerate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
